import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, Image as ImgIcon, Megaphone, ExternalLink, Download, FileText as FileIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import ImagePicker from "@/components/ImagePicker";

export default function AdsPage() {
  const [copyForm, setCopyForm] = useState({ destination: "", offer: "", audience: "", platform: "meta", variations: 3 });
  const [copyLoading, setCopyLoading] = useState(false);
  const [variants, setVariants] = useState([]);

  const [bannerForm, setBannerForm] = useState({ destination: "", offer: "", style: "moderno, colorido, sensación de aventura" });
  const [bannerLoading, setBannerLoading] = useState(false);
  const [banners, setBanners] = useState([]);

  const [campaigns, setCampaigns] = useState([]);
  const [landings, setLandings] = useState([]);
  const [apiResult, setApiResult] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    name: "", platform: "meta", objective: "Conversiones",
    destination: "", offer: "", audience: "",
    budget_daily: 50, start_date: new Date().toISOString().slice(0, 10),
    funnel_type: "landing", landing_id: "",
  });

  const load = () => {
    api.get("/ads/campaigns").then((r) => setCampaigns(r.data));
    api.get("/landings").then((r) => setLandings(r.data));
  };

  useEffect(() => { load(); }, []);

  const generateCopy = async () => {
    if (!copyForm.destination || !copyForm.offer) return toast.error("Completa destino y oferta");
    setCopyLoading(true);
    try {
      const r = await api.post("/ads/copy", copyForm);
      setVariants(r.data.variants);
      toast.success("Copy generado");
    } catch {
      toast.error("Error generando copy");
    } finally { setCopyLoading(false); }
  };

  const generateBanner = async () => {
    if (!bannerForm.destination) return toast.error("Indica el destino");
    setBannerLoading(true);
    try {
      const r = await api.post("/ads/banner", bannerForm);
      setBanners(r.data.images);
      toast.success("Banners generados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando banner");
    } finally { setBannerLoading(false); }
  };

  const createCampaign = async () => {
    if (!campaignForm.name || !campaignForm.destination) return toast.error("Completa los campos clave");
    const payload = {
      ...campaignForm,
      budget_daily: Number(campaignForm.budget_daily),
      landing_id: campaignForm.landing_id || null,
      ad_copy: variants,
      banner_image: banners[0] || null,
    };
    await api.post("/ads/campaigns", payload);
    toast.success("Campaña creada");
    setCampaignForm({ ...campaignForm, name: "" });
    load();
  };

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "paused" : "active";
    await api.patch(`/ads/campaigns/${c.id}`, { status: next });
    toast.success(`Campaña ${next === "active" ? "activada" : "pausada"}`);
    load();
  };

  const downloadExport = async (campaign) => {
    const platform = campaign.platform;
    const url = `${API_BASE}/ads/export/${campaign.id}/${platform}`;
    const token = localStorage.getItem("voyagemarkt_token");
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Error descargando export");
      const blob = await resp.blob();
      const filename = platform === "meta" ? `meta_${campaign.name}.json` : `google_${campaign.name}.csv`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename.replace(/\s+/g, "_");
      link.click();
      toast.success("Export descargado");
    } catch (e) {
      toast.error("No se pudo descargar el export");
    }
  };

  const [instructions, setInstructions] = useState(null);
  const showInstructions = async (c) => {
    const r = await api.get(`/ads/export/${c.id}/instructions`);
    setInstructions({ campaign: c, ...r.data });
  };

  const publishApi = async (c, platform) => {
    toast.info(`Publicando en ${platform === "meta" ? "Meta Ads" : "Google Ads"}...`);
    try {
      const r = await api.post(`/ads/campaigns/${c.id}/publish-${platform}`);
      setApiResult({ ...r.data, platform });
      toast.success(`Campaña enviada (${r.data.mode})`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error publicando");
    }
  };

  return (
    <div className="stagger">
      <PageHeader
        overline="Campañas Publicitarias"
        title="Studio de anuncios."
        subtitle="Genera copy y banners con IA, configura el funnel y lanza campañas en Meta y Google Ads."
      />

      <Tabs defaultValue="copy" className="space-y-6">
        <TabsList className="bg-[#F9F8F6] border border-[#E2DCD0] rounded-sm">
          <TabsTrigger value="copy" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="ads-tab-copy">Copy IA</TabsTrigger>
          <TabsTrigger value="banner" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="ads-tab-banner">Banners IA</TabsTrigger>
          <TabsTrigger value="campaigns" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="ads-tab-campaigns">Campañas</TabsTrigger>
        </TabsList>

        <TabsContent value="copy">
          <Card className="edit-card p-6 rounded-sm shadow-none">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Destino</Label>
                <Input data-testid="ads-copy-destination" value={copyForm.destination} onChange={(e) => setCopyForm({ ...copyForm, destination: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Oferta</Label>
                <Input value={copyForm.offer} onChange={(e) => setCopyForm({ ...copyForm, offer: e.target.value })} placeholder="7 días desde 1.200€" className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Audiencia</Label>
                <Input value={copyForm.audience} onChange={(e) => setCopyForm({ ...copyForm, audience: e.target.value })} placeholder="Parejas 30-45" className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Plataforma</Label>
                <Select value={copyForm.platform} onValueChange={(v) => setCopyForm({ ...copyForm, platform: v })}>
                  <SelectTrigger className="mt-1 rounded-sm bg-white border-[#E2DCD0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meta">Meta (Facebook/Instagram)</SelectItem>
                    <SelectItem value="google">Google Ads</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="ads-copy-generate" onClick={generateCopy} disabled={copyLoading} className="mt-5 bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {copyLoading ? "Escribiendo..." : "Generar variantes"}
            </Button>

            {variants.length > 0 && (
              <div className="mt-7 grid md:grid-cols-3 gap-4">
                {variants.map((v, i) => (
                  <div key={i} className="border border-[#E2DCD0] rounded-sm p-4 bg-[#F9F8F6]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-2">Variante {i + 1}</p>
                    <h4 className="font-display text-lg text-[#0A1128] leading-tight">{v.headline}</h4>
                    <p className="text-sm text-[#5C616A] mt-2">{v.description}</p>
                    <Badge className="mt-3 bg-[#D95D39] text-white rounded-sm">{v.cta}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="banner">
          <Card className="edit-card p-6 rounded-sm shadow-none">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Destino</Label>
                <Input data-testid="ads-banner-destination" value={bannerForm.destination} onChange={(e) => setBannerForm({ ...bannerForm, destination: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Oferta</Label>
                <Input value={bannerForm.offer} onChange={(e) => setBannerForm({ ...bannerForm, offer: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Estilo visual</Label>
                <Input value={bannerForm.style} onChange={(e) => setBannerForm({ ...bannerForm, style: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
              </div>
            </div>
            <Button data-testid="ads-banner-generate" onClick={generateBanner} disabled={bannerLoading} className="mt-5 bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm">
              <ImgIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {bannerLoading ? "Generando con Nano Banana..." : "Generar banners"}
            </Button>

            {banners.length > 0 && (
              <div className="mt-7 grid md:grid-cols-2 gap-4">
                {banners.map((b, i) => (
                  <div key={i} className="border border-[#E2DCD0] rounded-sm overflow-hidden bg-white">
                    <img src={b} alt={`banner-${i}`} className="w-full h-auto" />
                  </div>
                ))}
              </div>
            )}

            <div className="mt-7 pt-7 border-t border-[#E2DCD0]">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-3">
                ...o elige una imagen de tu biblioteca
              </p>
              <ImagePicker
                label=""
                value={banners[0] || ""}
                onChange={(url) => setBanners(url ? [url] : [])}
                testid="ads-banner-picker"
              />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="edit-card p-6 rounded-sm shadow-none">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98]">Nueva</p>
              <h3 className="font-display text-2xl text-[#0A1128] mt-1 mb-5">Crear campaña</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Nombre</Label>
                    <Input data-testid="campaign-name" value={campaignForm.name} onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Plataforma</Label>
                    <Select value={campaignForm.platform} onValueChange={(v) => setCampaignForm({ ...campaignForm, platform: v })}>
                      <SelectTrigger className="mt-1 rounded-sm bg-white border-[#E2DCD0]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="meta">Meta Ads</SelectItem>
                        <SelectItem value="google">Google Ads</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Destino</Label>
                    <Input value={campaignForm.destination} onChange={(e) => setCampaignForm({ ...campaignForm, destination: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Audiencia</Label>
                    <Input value={campaignForm.audience} onChange={(e) => setCampaignForm({ ...campaignForm, audience: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Oferta</Label>
                  <Input value={campaignForm.offer} onChange={(e) => setCampaignForm({ ...campaignForm, offer: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Budget diario (€)</Label>
                    <Input type="number" value={campaignForm.budget_daily} onChange={(e) => setCampaignForm({ ...campaignForm, budget_daily: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Inicio</Label>
                    <Input type="date" value={campaignForm.start_date} onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Funnel</Label>
                    <Select value={campaignForm.funnel_type} onValueChange={(v) => setCampaignForm({ ...campaignForm, funnel_type: v })}>
                      <SelectTrigger className="mt-1 rounded-sm bg-white border-[#E2DCD0]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="landing">Landing + formulario</SelectItem>
                        <SelectItem value="instant_form">Formulario instantáneo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Landing</Label>
                    <Select value={campaignForm.landing_id || "none"} onValueChange={(v) => setCampaignForm({ ...campaignForm, landing_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-1 rounded-sm bg-white border-[#E2DCD0]"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Ninguna —</SelectItem>
                        {landings.map((l) => (
                          <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-[#8A8F98]">
                  Adjuntará automáticamente las variantes de copy y el primer banner generados arriba.
                </p>
                <Button data-testid="campaign-create-button" onClick={createCampaign} className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">
                  <Megaphone className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Crear campaña
                </Button>
              </div>
            </Card>

            <div className="space-y-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98]">Tus campañas</p>
              {campaigns.length === 0 && (
                <Card className="edit-card p-8 rounded-sm shadow-none text-center text-[#5C616A]">
                  Aún no hay campañas creadas.
                </Card>
              )}
              {campaigns.map((c) => (
                <Card key={c.id} className="edit-card p-5 rounded-sm shadow-none">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="rounded-sm text-[10px] border-[#0A1128] text-[#0A1128]">{c.platform.toUpperCase()}</Badge>
                        <Badge variant="outline" className={`rounded-sm text-[10px] ${c.status === "active" ? "border-[#2D6A4F] text-[#2D6A4F]" : "border-[#8A8F98] text-[#8A8F98]"}`}>{c.status}</Badge>
                      </div>
                      <h4 className="font-display text-lg text-[#0A1128] truncate">{c.name}</h4>
                      <p className="text-sm text-[#5C616A]">{c.destination} · {c.offer}</p>
                      <div className="mt-2 text-xs text-[#8A8F98] flex gap-3 font-mono">
                        <span>€{c.budget_daily}/día</span>
                        <span>Funnel: {c.funnel_type}</span>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => toggleStatus(c)} className="rounded-sm border-[#E2DCD0] shrink-0" data-testid={`campaign-toggle-${c.id}`}>
                      {c.status === "active" ? "Pausar" : "Activar"}
                    </Button>
                  </div>
                  {c.banner_image && (
                    <img src={c.banner_image} alt="banner" className="mt-3 w-full h-24 object-cover rounded-sm" />
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => downloadExport(c)} className="rounded-sm border-[#E2DCD0]" data-testid={`campaign-export-${c.id}`}>
                      <Download className="w-3 h-3 mr-1" />
                      {c.platform === "meta" ? "Export JSON" : "Export CSV"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => showInstructions(c)} className="rounded-sm border-[#E2DCD0]">
                      <FileIcon className="w-3 h-3 mr-1" />Cómo publicar
                    </Button>
                    <Button size="sm" onClick={() => publishApi(c, c.platform)} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm" data-testid={`campaign-publish-api-${c.id}`}>
                      <Sparkles className="w-3 h-3 mr-1" />Publicar vía API
                    </Button>
                    {c.meta_campaign_id && c.platform === "meta" && (
                      <Badge variant="outline" className="rounded-sm border-[#2D6A4F] text-[#2D6A4F] text-[10px]"><Check className="w-2.5 h-2.5 mr-1" />Meta: {c.meta_campaign_id}</Badge>
                    )}
                    {c.google_campaign_id && c.platform === "google" && (
                      <Badge variant="outline" className="rounded-sm border-[#2D6A4F] text-[#2D6A4F] text-[10px]"><Check className="w-2.5 h-2.5 mr-1" />Google: {c.google_campaign_id}</Badge>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Instructions modal */}
      <Dialog open={!!instructions} onOpenChange={(o) => !o && setInstructions(null)}>
        <DialogContent className="bg-white rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              Cómo publicar en {instructions?.platform === "meta" ? "Meta Ads" : "Google Ads"}
            </DialogTitle>
          </DialogHeader>
          <ol className="space-y-3 text-sm">
            {(instructions?.steps || []).map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-mono text-[#D95D39] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <p className="font-medium text-[#0A1128]">{s.title}</p>
                  <p className="text-[#5C616A]">{s.detail}</p>
                  {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="text-xs text-[#D95D39] hover:underline">Abrir →</a>}
                </div>
              </li>
            ))}
          </ol>
        </DialogContent>
      </Dialog>

      {/* API publish result modal */}
      <Dialog open={!!apiResult} onOpenChange={(o) => !o && setApiResult(null)}>
        <DialogContent className="bg-white rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              Publicación {apiResult?.platform === "meta" ? "Meta Ads" : "Google Ads"}
              <Badge variant="outline" className={`rounded-sm text-[10px] ${apiResult?.mode === "MOCKED_READY" ? "border-[#D95D39] text-[#D95D39]" : "border-[#8A8F98] text-[#8A8F98]"}`}>{apiResult?.mode}</Badge>
            </DialogTitle>
          </DialogHeader>
          {apiResult?.mode?.startsWith("MOCKED") && (
            <div className="p-3 bg-[#F9F8F6] border border-[#D95D39] rounded-sm text-xs text-[#5C616A] mb-3">
              <strong className="text-[#9D0208]">Modo simulado.</strong> Esta respuesta imita la estructura real de la API. Cuando configures tus credenciales en Settings, las campañas se crearán de verdad. La estructura completa devuelta abajo está lista para uso real.
            </div>
          )}
          <pre className="text-[11px] font-mono bg-[#0A1128] text-[#E2DCD0] p-4 rounded-sm overflow-x-auto">
            {JSON.stringify(apiResult, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
